import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GetPnlsRequest, GetPnlsResponse } from '@amzn/finspace-service-typescript-client';

interface CachedPnLData {
  data: GetPnlsResponse;
  timestamp: number;
  request: GetPnlsRequest;
}

interface PnLState {
  cachedData: Map<string, CachedPnLData>;
  isLoading: boolean;
  error: string | null;
}

interface PnLActions {
  getPnL: (request: GetPnlsRequest) => Promise<GetPnlsResponse | null>;
  clearCache: () => void;
  clearError: () => void;
}

type PnLStore = PnLState & PnLActions;

const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 1 day in milliseconds

const getCacheKey = (request: GetPnlsRequest): string => {
  // Create a stable cache key based on the request filters
  const filters = request.filters;
  if (!filters) {
    return 'no-filters';
  }
  
  const reportingLines = (filters.reportingLines || []).sort().join(',');
  const periods = (filters.periods || []).sort().join(',');
  const scenario = (filters.scenario || []).sort().join(',');
  
  return `${reportingLines}|${periods}|${scenario}`;
};

const isDataStale = (timestamp: number): boolean => {
  return Date.now() - timestamp > CACHE_DURATION_MS;
};

export const usePnLStore = create<PnLStore>()(
  persist(
    (set, get) => ({
      // State
      cachedData: new Map(),
      isLoading: false,
      error: null,

      // Actions
      getPnL: async (request: GetPnlsRequest) => {
        const cacheKey = getCacheKey(request);
        const cached = get().cachedData.get(cacheKey);

        // Return cached data if fresh
        if (cached && !isDataStale(cached.timestamp)) {
          return cached.data;
        }

        // Fetch new data
        set({ isLoading: true, error: null });

        try {
          const { FinSpaceServiceApi } = await import('../api/finspaceServiceApi');
          const response = await FinSpaceServiceApi.getPnls(request);

          // Only cache if response has actual data
          const hasData = response?.pnl && response.pnl.length > 0;

          if (hasData) {
            const newCache = new Map(get().cachedData);
            newCache.set(cacheKey, {
              data: response,
              timestamp: Date.now(),
              request,
            });

            set({
              cachedData: newCache,
              isLoading: false,
            });
          } else {
            // Don't cache empty responses - allow retry on next load
            set({
              isLoading: false,
            });
          }

          return response;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch PnL data';
          set({
            isLoading: false,
            error: errorMessage,
          });
          return null;
        }
      },

      clearCache: () => {
        set({ cachedData: new Map() });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'pnl-storage',
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;

          try {
            const { state } = JSON.parse(str);
            // Convert cachedData array back to Map
            return {
              state: {
                ...state,
                cachedData: new Map(state.cachedData || []),
              },
            };
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          const { state } = value;
          // Convert Map to array for storage
          const storableState = {
            ...state,
            cachedData: Array.from(state.cachedData.entries()),
          };
          sessionStorage.setItem(name, JSON.stringify({ state: storableState }));
        },
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    }
  )
);
